import {useEffect, useMemo, useState} from 'react';
import md5 from 'md5';
import {shallowEqual} from 'react-redux';
import type {ItemObject} from '@PosterWhiteboard/items/item/item.types';
import {getCurrentPageItems, selectIsCurrentPageModifying} from '@Components/poster-editor/poster-editor-reducer';
import {maxDimensionFor} from '@Hooks/poster-editor/useItemThumbDataURL';
import type {CachedThumbnail} from '@Hooks/poster-editor/useItemThumbDataURL';
import {useAppSelector} from '@/hooks';
import type {RootState} from '@/store';

const GROUP_MEMBER_PROPERTIES_TO_EXCLUDE = new Set(['onPageStartTime', 'onPageEndTime', 'timelineSubRow']);

const groupThumbnailCache = new Map<string, CachedThumbnail>();
const EMPTY_GROUP_MEMBERS: ItemObject[] = [];

function getHashableGroupMemberProperties(itemObject: ItemObject): Record<string, unknown> {
  // left/top are kept here (unlike standalone item hashing) because a member's position relative to its group-mates affects the group thumbnail
  const filtered: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(itemObject)) {
    if (!GROUP_MEMBER_PROPERTIES_TO_EXCLUDE.has(key)) {
      filtered[key] = value;
    }
  }
  return filtered;
}

export function useGroupThumbDataURL(groupId: string): string | null {
  const members = useAppSelector((state: RootState): ItemObject[] => {
    const items = getCurrentPageItems(state);
    if (!items) {
      return EMPTY_GROUP_MEMBERS;
    }
    const result: ItemObject[] = [];
    for (const uid of items.itemsOrder) {
      const item = items.itemsHashMap[uid];
      if (item?.groupId === groupId) {
        result.push(item);
      }
    }
    return result;
  }, shallowEqual);
  const angle = useAppSelector((state: RootState): number => getCurrentPageItems(state)?.groups.groupsHashMap[groupId]?.angle ?? 0);
  const isModifying = useAppSelector(selectIsCurrentPageModifying);

  const hash = useMemo((): string | null => {
    if (members.length === 0) {
      return null;
    }
    return md5(JSON.stringify({angle, members: members.map(getHashableGroupMemberProperties)}));
  }, [members, angle]);

  const [thumb, setThumb] = useState<string | null>(() => groupThumbnailCache.get(groupId)?.url ?? null);

  useEffect((): (() => void) | undefined => {
    if (!hash) {
      setThumb(null);
      return undefined;
    }
    if (isModifying) {
      return undefined;
    }
    const cached = groupThumbnailCache.get(groupId);
    if (cached?.hash === hash) {
      setThumb(cached.url);
      return undefined;
    }
    const page = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!page) {
      return undefined;
    }

    let cancelled = false;

    void (async (): Promise<void> => {
      try {
        const bbox = page.items.groups.getBoundingBoxForGroup(groupId);
        const url = await page.items.groups.toDataURL(groupId, {maxDimension: bbox ? maxDimensionFor(bbox.width, bbox.height) : undefined});
        if (cancelled) {
          return;
        }
        if (url) {
          groupThumbnailCache.set(groupId, {url, hash});
          setThumb(url);
        } else {
          setThumb(null);
        }
      } catch {
        if (!cancelled) {
          setThumb(null);
        }
      }
    })();

    return (): void => {
      cancelled = true;
    };
  }, [groupId, hash, isModifying]);

  return thumb;
}
